import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from "react";
import {
  apiClient,
  type CopilotoConversation,
  type CopilotoMessage,
  type CopilotoMode,
  type CopilotoStreamEvent
} from "@/shared/api/api-client";

import { ApiError, errorMessage } from "@/shared/api/http";
export type CopilotoChatMessage = CopilotoMessage & {
  pending?: boolean;
  error?: boolean;
  /** Pergunta que a pessoa não escreveu (a instrução dos chips de
   *  follow-up): vai pro servidor e entra na lista, mas a conversa não
   *  a mostra. Existe pra "Gerar outra resposta" achar a pergunta que
   *  GEROU a resposta — sem ela, a busca "última mensagem de usuário"
   *  caía na pergunta original e reenviava o assunto errado. */
  hidden?: boolean;
};

/** A mensagem já tem id do servidor? Enquanto não tem (o `done` é quem
 *  troca o `local-…`), qualquer rota que leve o id no caminho —
 *  `PUT /messages/{id}` — receberia um id que não existe. */
export function hasServerId(message: CopilotoMessage): boolean {
  return !message.id.startsWith("local-");
}

/** Como o pendente vira erro, em qualquer um dos três fluxos. */
function markFailed(
  messages: CopilotoChatMessage[],
  pendingId: string,
  text: string
): CopilotoChatMessage[] {
  return messages.map(message =>
    message.id === pendingId
      ? { ...message, content: text, error: true, pending: false }
      : message
  );
}


type CopilotoContextValue = {
  conversations: CopilotoConversation[];
  activeConversationId: string | null;
  messages: CopilotoChatMessage[];
  sending: boolean;
  /** Instante (epoch ms) até o qual a cota está estourada, ou `null`.
   *
   *  Vem do `Retry-After` de um 429. Existe pra tela poder TRAVAR a
   *  caixa de escrever com uma contagem em vez de só mostrar o erro:
   *  sem isso a pessoa clica de novo, e cada clique é outro 429 — o
   *  Copiloto é a única tela em que o limite foi desenhado pra morder,
   *  então é a única que paga por essa contagem. */
  blockedUntil: number | null;
  /** Anota a pausa de cota quando o erro for um 429 (devolve se era).
   *  Exposto porque a cota é da PESSOA, não do chat. (O ditado por voz,
   *  que também a usava, foi removido em 2026-10-09.) */
  noteQuota: (caught: unknown) => boolean;
  /** Sempre "padrao" (modo interno): a escolha Padrão/Web saiu das
   *  Preferências em 2026-10-13, e a Mestra Help só fala pelo modo interno. */
  defaultMode: CopilotoMode;
  /** Texto do erro da última carga da lista de conversas, ou `null`. */
  conversationsError: string | null;
  icebreakers: string[];
  loadConversations: () => Promise<void>;
  selectConversation: (id: string | null) => Promise<void>;
  startNewConversation: () => void;
  sendMessage: (content: string, mode: CopilotoMode, options?: { visible?: boolean }) => Promise<void>;
  regenerateMessage: (messageId: string, mode?: CopilotoMode) => Promise<void>;
  editMessage: (messageId: string, content: string) => Promise<void>;
  removeConversation: (id: string) => Promise<void>;
};

// Este Context era guardado em `globalThis` porque o `@vitejs/plugin-rsc`
// avaliava o módulo em grafos separados (`rsc`/`ssr`/`client`) e criava
// dois `createContext` distintos — o provider escrevia num, o
// `useContext` lia o outro. Sem RSC existe um grafo só, e a gambiarra
// deixou de ser necessária: é a mesma remoção que o `AppShellProvider`
// já tinha feito e documentado.
const CopilotoContext = createContext<CopilotoContextValue | null>(null);

export function CopilotoProvider({ token, children }: { token: string; children: ReactNode }) {
  const [conversations, setConversations] = useState<CopilotoConversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<CopilotoChatMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [blockedUntil, setBlockedUntil] = useState<number | null>(null);
  const defaultMode: CopilotoMode = "padrao";
  const [conversationsError, setConversationsError] = useState<string | null>(null);
  // Carga mais recente de "abrir conversa". Ver `selectConversation`.
  const openSeq = useRef(0);

  /** Anota até quando esperar, quando o erro for de cota. Devolve se
   *  ERA um 429 — quem chama usa isso pra decidir entre desfazer o que
   *  já mexeu na tela (a cota recusou ANTES de o servidor fazer
   *  qualquer coisa) e mostrar o erro no lugar. */
  const noteQuota = useCallback((caught: unknown): boolean => {
    if (!(caught instanceof ApiError) || caught.status !== 429) return false;
    setBlockedUntil(
      Date.now() + (caught.retryAfterSeconds ?? 60) * 1000
    );
    return true;
  }, []);
  const [icebreakers, setIcebreakers] = useState<string[]>([]);
  const loadedIcebreakers = useRef(false);

  // Nunca rejeita: os chamadores são `void` (o efeito de montagem e o
  // `done` do stream), e uma rejeição sem dono deixava a lista vazia
  // dizendo "Nenhuma conversa ainda." — igual a quem nunca conversou
  // (2026-10-09, teste de render). A falha vira estado que a página mostra.
  const loadConversations = useCallback(async () => {
    try {
      const list = await apiClient.copilotoConversations(token);
      setConversations(list);
      setConversationsError(null);
    } catch (caught) {
      setConversationsError(errorMessage(caught, "Não consegui carregar as conversas."));
    }
  }, [token]);

  /**
   * O laço que lê o stream do Copiloto, num lugar só.
   *
   * Os três fluxos (enviar, regerar, editar) tinham o mesmo `for await`
   * com os mesmos três ramos — ~35 linhas copiadas três vezes — e a
   * única diferença real é o que o `done` faz ALÉM de fechar o
   * pendente: enviar também troca o id da mensagem do usuário e renomeia
   * a conversa. É isso que `onDone` recebe.
   *
   * O `delta` acumula em vez de substituir: cada evento traz só o
   * pedaço novo.
   */
  const consumeStream = useCallback(
    async (
      events: AsyncIterable<CopilotoStreamEvent>,
      pendingId: string,
      onDone?: (event: Extract<CopilotoStreamEvent, { type: "done" }>) => void
    ) => {
      // Terminou por `done` ou por `error`? Conexão que fecha LIMPA sem
      // nenhum dos dois (proxy cortando por ociosidade) não lança, e a
      // bolha ficava "Pensando…" pra sempre (2026-10-09, teste de render).
      let settled = false;
      for await (const event of events) {
        if (event.type === "delta") {
          setMessages(current =>
            current.map(message =>
              message.id === pendingId
                ? { ...message, content: message.content + event.text }
                : message
            )
          );
        } else if (event.type === "error") {
          // A MENSAGEM do erro, não o parcial: o servidor não grava
          // resposta truncada, e o `||` que mantinha o texto cortado
          // fazia uma frase pela metade parecer a resposta (2026-10-09).
          settled = true;
          setMessages(current => markFailed(current, pendingId, event.message));
        } else if (event.type === "done") {
          settled = true;
          setMessages(current =>
            current.map(message =>
              message.id === pendingId
                ? { ...message, id: event.messageId ?? message.id, pending: false }
                : message
            )
          );
          onDone?.(event);
          void loadConversations();
        }
      }
      if (!settled) {
        setMessages(current =>
          markFailed(current, pendingId, "A resposta foi interrompida. Tente gerar de novo.")
        );
      }
    },
    [loadConversations]
  );

  useEffect(() => {
    void loadConversations();
    if (!loadedIcebreakers.current) {
      loadedIcebreakers.current = true;
      apiClient
        .copilotoIcebreakers(token)
        .then(result => setIcebreakers(result.icebreakers))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Descarte por CARGA (a forma de `use-mention-filters.ts`): clicar A
  // (lenta) e depois B (rápida) deixava a resposta de A chegar por
  // último e sobrescrever a tela de B. A conversa vira ativa NA HORA e a
  // lista anterior sai da tela — se a busca falhar, as mensagens de A
  // não podem ficar sob o cabeçalho de B, com o próximo envio indo pra B
  // (2026-10-09, teste de render).
  const selectConversation = useCallback(
    async (id: string | null) => {
      const seq = ++openSeq.current;
      setActiveConversationId(id);
      setMessages([]);
      if (!id) return;
      try {
        const list = await apiClient.copilotoMessages(token, id);
        if (seq === openSeq.current) setMessages(list);
      } catch (caught) {
        if (seq !== openSeq.current) return;
        setMessages([
          {
            id: `local-open-error-${id}`,
            role: "assistant",
            content: errorMessage(caught, "Não consegui abrir esta conversa."),
            mode: null,
            created_at: new Date().toISOString(),
            error: true
          }
        ]);
      }
    },
    [token]
  );

  const startNewConversation = useCallback(() => {
    openSeq.current += 1;
    setActiveConversationId(null);
    setMessages([]);
  }, []);

  const removeConversation = useCallback(
    async (id: string) => {
      await apiClient.deleteCopilotoConversation(token, id);
      if (activeConversationId === id) openSeq.current += 1;
      setConversations(current => current.filter(item => item.id !== id));
      setActiveConversationId(current => (current === id ? null : current));
      setMessages(current => (activeConversationId === id ? [] : current));
    },
    [token, activeConversationId]
  );

  const sendMessage = useCallback(
    async (content: string, mode: CopilotoMode, options?: { visible?: boolean }) => {
      const trimmed = content.trim();
      if (!trimmed || sending) return;
      setSending(true);

      // Declarado FORA do `try` porque o `catch` precisa dele: a versão
      // anterior procurava "a última pendente" justamente por não
      // alcançar esta variável, e aquilo era contorno de escopo, não
      // regra.
      const now = new Date().toISOString();
      const pendingId = `local-assistant-${now}`;
      const userMessage: CopilotoChatMessage = {
        id: `local-user-${now}`,
        role: "user",
        content: trimmed,
        mode,
        created_at: now,
        hidden: options?.visible === false
      };

      try {
        let conversationId = activeConversationId;
        if (!conversationId) {
          const created = await apiClient.createCopilotoConversation(token);
          conversationId = created.id;
          setActiveConversationId(conversationId);
          setConversations(current => [created, ...current]);
        }

        const assistantMessage: CopilotoChatMessage = {
          id: pendingId,
          role: "assistant",
          content: "",
          mode,
          created_at: now,
          pending: true
        };
        setMessages(current => [...current, userMessage, assistantMessage]);

        await consumeStream(
          apiClient.streamCopilotoMessage(token, conversationId, trimmed, mode),
          pendingId,
          event => {
            // Só o envio faz estas duas coisas a mais: a mensagem do
            // usuário também nasceu local e precisa do id de verdade, e
            // a primeira resposta é o que batiza a conversa.
            setMessages(current =>
              current.map(message =>
                message.id === userMessage.id
                  ? { ...message, id: event.userMessageId ?? message.id }
                  : message
              )
            );
            if (event.title) {
              setConversations(current =>
                current.map(item =>
                  item.id === conversationId ? { ...item, title: event.title } : item
                )
              );
            }
          }
        );
      } catch (caught) {
        noteQuota(caught);
        const message = errorMessage(caught, "Não consegui responder agora.");
        setMessages(current => {
          if (current.some(item => item.id === pendingId)) {
            return markFailed(current, pendingId, message);
          }
          // Falhou ANTES de a bolha existir (criar a conversa deu 500): a
          // pergunta já saiu da caixa e nada na tela dizia que se perdeu
          // (2026-10-09, teste de render).
          return [
            ...current,
            ...(userMessage.hidden ? [] : [userMessage]),
            { id: pendingId, role: "assistant", content: message, mode, created_at: now, error: true }
          ];
        });
      } finally {
        setSending(false);
      }
    },
    [token, activeConversationId, sending, loadConversations, noteQuota]
  );

  const regenerateMessage = useCallback(
    async (messageId: string, mode?: CopilotoMode) => {
      if (sending || !activeConversationId) return;
      const index = messages.findIndex(m => m.id === messageId);
      if (index === -1) return;

      // A pergunta que GEROU esta resposta: a mensagem de usuário que a
      // precede, escrita pela pessoa ou não (o follow-up é `hidden`).
      let question: CopilotoChatMessage | undefined;
      for (let i = index - 1; i >= 0; i--) {
        if (messages[i].role === "user") {
          question = messages[i];
          break;
        }
      }
      // Sem id do servidor não há o que rebranchar (ver `hasServerId`).
      if (!question || !question.content || !hasServerId(question)) return;

      setSending(true);
      const targetMode = mode ?? messages[index].mode ?? "padrao";
      const pendingId = `local-regenerate-${Date.now()}`;
      const now = new Date().toISOString();

      // A lista de ANTES do corte. "Gerar outra resposta" apaga a
      // resposta que estava lá antes de saber se vem outra — e com a
      // cota valendo, três cliques seguidos fazem o terceiro tomar 429 e
      // trocar uma resposta boa por um balão de erro. Num 429 o servidor
      // não fez nada, então desfazer é a verdade.
      const antes = messages;

      setMessages(current => {
        const idx = current.findIndex(m => m.id === messageId);
        if (idx === -1) return current;
        const next = current.slice(0, idx);
        return [
          ...next,
          { id: pendingId, role: "assistant", content: "", mode: targetMode, created_at: now, pending: true }
        ];
      });

      try {
        await consumeStream(
          // Rebranch (`PUT /messages/{id}`), o mesmo da edição: o servidor
          // corta a cauda e responde de novo SEM apendar outra pergunta.
          // O POST de envio duplicava a pergunta no histórico e deixava a
          // resposta velha no contexto do modelo (2026-10-09, teste de
          // render). O PUT não leva modo — vale o do servidor.
          apiClient.editCopilotoMessage(
            token,
            activeConversationId,
            question.id,
            question.content
          ),
          pendingId
        );
      } catch (caught) {
        if (noteQuota(caught)) {
          setMessages(antes);
          return;
        }
        const message = errorMessage(caught, "Não consegui responder agora.");
        setMessages(current => markFailed(current, pendingId, message));
      } finally {
        setSending(false);
      }
    },
    [
      token,
      activeConversationId,
      sending,
      messages,
      loadConversations,
      noteQuota
    ]
  );

  const editMessage = useCallback(
    async (messageId: string, content: string) => {
      const trimmed = content.trim();
      const conversationId = activeConversationId;
      if (!trimmed || sending || !conversationId) return;
      setSending(true);

      // Igual ao regenerar: editar CORTA a cauda antes de saber se a
      // resposta vem. Num 429 o servidor não aplicou nada, e sem
      // desfazer a tela mostraria a conversa truncada até alguém
      // reselecioná-la.
      const antes = messages;

      setMessages(current => {
        const index = current.findIndex(message => message.id === messageId);
        if (index === -1) return current;
        return current
          .slice(0, index + 1)
          .map(message => (message.id === messageId ? { ...message, content: trimmed } : message));
      });

      const pendingId = `local-assistant-edit-${messageId}`;
      const now = new Date().toISOString();
      setMessages(current => [
        ...current,
        { id: pendingId, role: "assistant", content: "", mode: null, created_at: now, pending: true }
      ]);

      try {
        await consumeStream(
          apiClient.editCopilotoMessage(
            token,
            conversationId,
            messageId,
            trimmed
          ),
          pendingId
        );
      } catch (caught) {
        if (noteQuota(caught)) {
          setMessages(antes);
          return;
        }
        const message = errorMessage(caught, "Não consegui responder agora.");
        setMessages(current => markFailed(current, pendingId, message));
      } finally {
        setSending(false);
      }
    },
    [
      token,
      activeConversationId,
      sending,
      messages,
      loadConversations,
      noteQuota
    ]
  );

  const value = useMemo<CopilotoContextValue>(
    () => ({
      conversations,
      activeConversationId,
      messages,
      sending,
      blockedUntil,
      noteQuota,
      defaultMode,
      conversationsError,
      icebreakers,
      loadConversations,
      selectConversation,
      startNewConversation,
      sendMessage,
      regenerateMessage,
      editMessage,
      removeConversation
    }),
    [
      conversations,
      activeConversationId,
      messages,
      sending,
      blockedUntil,
      noteQuota,
      defaultMode,
      conversationsError,
      icebreakers,
      loadConversations,
      selectConversation,
      startNewConversation,
      sendMessage,
      regenerateMessage,
      editMessage,
      removeConversation
    ]
  );

  return <CopilotoContext.Provider value={value}>{children}</CopilotoContext.Provider>;
}

export function useCopiloto(): CopilotoContextValue {
  const context = useContext(CopilotoContext);
  if (!context) throw new Error("useCopiloto must be used within a CopilotoProvider");
  return context;
}

/** Quantos segundos faltam da pausa de cota, ou `null` se não há pausa.
 *
 *  Reconta a cada segundo, e SÓ enquanto há pausa — um temporizador
 *  permanente aqui redesenharia o chat inteiro uma vez por segundo pra
 *  sempre, o resto do tempo à toa.
 *
 *  Existe só no Copiloto: é a única tela em que o limite foi desenhado
 *  pra morder de verdade, e portanto a única em que vale explicar
 *  quanto falta em vez de só dizer que não deu. */
export function useQuotaCountdown(): number | null {
  const { blockedUntil } = useCopiloto();
  const [, forceTick] = useState(0);

  useEffect(() => {
    if (blockedUntil === null || blockedUntil <= Date.now()) return;
    // O tick confere a expiração e desliga o PRÓPRIO intervalo: depois do
    // fim da pausa `blockedUntil` segue com o instante vencido, o efeito
    // não reroda, e o relógio continuava disparando a cada segundo pelo
    // resto da vida da página (2026-10-09, teste de render).
    const timer = window.setInterval(() => {
      if (Date.now() >= blockedUntil) window.clearInterval(timer);
      forceTick(n => n + 1);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [blockedUntil]);

  if (blockedUntil === null) return null;
  const faltam = Math.ceil((blockedUntil - Date.now()) / 1000);
  return faltam > 0 ? faltam : null;
}
